import { type CSSProperties, useState } from "react";
import { useNavigate } from "react-router";
import type { SettingsResponse, SettingsSending } from "../../shared/types";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { useToast } from "../components/ui/Toast";
import { formatAt } from "../lib/zones";
import { useSending } from "../sending/SendingContext";
import { SendingStatusPanel } from "../sending/SendingStatusPanel";
import { JoinInput, RecCard } from "../setup/parts";
import { rootOf } from "../setup/rules";
import { useSenderForm } from "../setup/useSenderForm";
import type { Run } from "../setup/useSettingsData";
import { SettingsHead } from "./SettingsView";

// Pause lines (fractions): spam over 0.3%, bounces over 4%, held by the email service for every sender.
export const SPAM_LIMIT = 0.003;
export const BOUNCE_LIMIT = 0.04;

export const pct = (rate: number) => `${Math.round(rate * 10000) / 100}%`;

/** Nothing has gone out yet: every rate is still zero, so none of them says anything. */
export function nothingSent(s: Pick<SettingsSending, "spam_rate" | "bounce_rate" | "delivered_rate">): boolean {
  return s.delivered_rate === 0 && s.bounce_rate === 0 && s.spam_rate === 0;
}

/** Sending is stopped by the email service until it is fixed. */
export const pausedNow = (s: SettingsSending) => s.state === "paused" || s.state === "final" || s.state === "stopped";

export const HEALTH_WORD: Record<SettingsSending["health_status"], string> = { healthy: "Healthy", warming: "Warming up", paused: "Paused" };

/** One line on how inboxes see the business's email, for the Settings card. */
export function sendingHealthLine(s: SettingsSending): string {
  if (nothingSent(s)) return "Nothing sent yet";
  return `${HEALTH_WORD[s.health_status] ?? "Healthy"}: ${pct(s.spam_rate)} spam complaints, ${pct(s.bounce_rate)} bounces`;
}

/** Settings > Sending email: the address emails come from, its records, who replies reach, and how inboxes see it. */
export function SendingPane({ data, run }: { data: SettingsResponse; run: Run }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { refresh } = useSending();
  const s = data.sending;
  const ok = s.status === "verified";
  const [showRecs, setShowRecs] = useState(false);
  const form = useSenderForm(data, run, { said: "Saved" });
  const test = async () => {
    const to = await form.sendTest();
    if (to) toast({ text: `Test sent to ${to}`, icon: "send" });
  };

  const focused = s.state === "paused" || s.state === "recovering" || s.state === "final" || s.state === "stopped";
  const checked = s.checked_at ? formatAt(s.checked_at * 1000, { weekday: "short", hour: "numeric", minute: "2-digit" }) : null;
  const root = s.domain ? rootOf(s.domain) : "";

  return (
    <>
      <SettingsHead eyebrow="Email" title="Sending email" />
      {focused || s.state === "headsup" ? <SendingStatusPanel s={s} run={run} refreshGlobal={refresh} /> : null}
      {focused ? null : (
        <>
          <section className="card ssec">
            <div className="card-h">
              <div>
                <h2>Send from your own address</h2>
              </div>
            </div>
            <div className="card-b">
              {ok ? (
                <>
                  <div className="row g12 wrap addrrow" style={{ padding: "14px 16px", borderRadius: 14, border: "1px solid var(--line)", background: "var(--surface-2)" }}>
                    <span className="face" style={{ "--s": "36px", background: "var(--good-soft)", color: "var(--good)" } as CSSProperties}>
                      <Icon name="shield" />
                    </span>
                    <div className="sp" style={{ minWidth: 0 }}>
                      <div className="b7">{s.domain}</div>
                      <div className="small muted">
                        All {s.records.length} records found{checked ? ` · checked ${checked}` : ""}
                      </div>
                    </div>
                    <Pill tone="good" dot>
                      Verified
                    </Pill>
                    <Button size="s" onClick={() => setShowRecs((v) => !v)}>
                      {showRecs ? "Hide" : "View"} the records
                    </Button>
                  </div>
                  {showRecs ? (
                    <div className="recs mt12">
                      {s.records.map((r) => (
                        <RecCard key={`${r.type} ${r.name}`} rec={r} root={root} state="found" />
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="note warn">
                  <Icon name="info" />
                  <div>
                    <b>Emails are off.</b>
                    <div className="mt8">
                      <Button size="s" variant="p" onClick={() => navigate(s.domain ? "/setup/email/records" : "/setup/email")}>
                        {s.domain ? "Finish it" : "Set it up"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid g3">
                <div className="field">
                  <label htmlFor="sd-name">From name</label>
                  <input dir="auto" id="sd-name" className="inp" value={form.fromName} onChange={(e) => form.setFromName(e.target.value)} onBlur={() => void form.saveName()} />
                </div>
                <div className="field">
                  <label htmlFor="sd-local">From address</label>
                  <JoinInput
                    id="sd-local"
                    value={form.local}
                    disabled={!s.domain}
                    onChange={form.setLocal}
                    onBlur={() => void form.saveLocal()}
                    suffix={s.domain ? `@${s.domain}` : "@ your sending address"}
                  />
                </div>
                <div className="field">
                  <label htmlFor="sd-reply">Replies go to</label>
                  <input id="sd-reply" className="inp" type="email" inputMode="email" value={form.reply} onChange={(e) => form.setReply(e.target.value)} onBlur={() => void form.saveReply()} />
                </div>
              </div>
              {ok ? (
                <div>
                  <Button size="s" icon="send" onClick={() => void test()}>
                    Send yourself a test email
                  </Button>
                </div>
              ) : null}
            </div>
          </section>
          <HealthCard s={s} />
        </>
      )}
    </>
  );
}

function Meter({ label, value, rate, limit, cap }: { label: string; value: string; rate: number; limit: number; cap: string }) {
  const over = rate > limit;
  // Why 60: the pause line sits at 60% of the track, so a rate at the line reaches it.
  const pos = Math.min(100, (rate / limit) * 60);
  return (
    <div className="hm">
      <div className="row">
        <span className="b6 small sp">{label}</span>
        <Pill tone={over ? "bad" : "good"}>{over ? "Over" : "Safe"}</Pill>
      </div>
      <b className="mt8" style={{ display: "block" }}>
        {value}
      </b>
      <div className="track">
        <i style={{ width: `${pos}%`, background: over ? "var(--bad)" : undefined }} />
        <em style={{ left: "60%" }} />
      </div>
      <div className="xs muted mt8">{cap}</div>
    </div>
  );
}

function HealthCard({ s }: { s: SettingsSending }) {
  const live = s.status === "verified" && !nothingSent(s);
  return (
    <section className="card ssec">
      <div className="card-h">
        <div>
          <h2>Sending health</h2>
        </div>
        {live ? (
          <Pill tone={s.health_status === "healthy" ? "good" : s.health_status === "paused" ? "bad" : "warn"} dot>
            {HEALTH_WORD[s.health_status] ?? "Healthy"}
          </Pill>
        ) : (
          <Pill>Nothing sent yet</Pill>
        )}
      </div>
      <div className="card-b">
        {live ? (
          <div className="meter3">
            <Meter label="Spam complaints" value={pct(s.spam_rate)} rate={s.spam_rate} limit={SPAM_LIMIT} cap="Pauses above 0.3%" />
            <Meter label="Bounces" value={pct(s.bounce_rate)} rate={s.bounce_rate} limit={BOUNCE_LIMIT} cap="Pauses above 4%" />
            <div className="hm">
              <div className="row">
                <span className="b6 small sp">Delivered</span>
                <Pill tone="good">Good</Pill>
              </div>
              <b className="mt8" style={{ display: "block" }}>
                {pct(s.delivered_rate)}
              </b>
              <div className="track">
                <i style={{ width: `${Math.min(100, s.delivered_rate * 100)}%` }} />
              </div>
            </div>
          </div>
        ) : (
          <div className="meter3">
            {["Spam complaints", "Bounces", "Delivered"].map((l) => (
              <div key={l} className="hm">
                <div className="b6 small">{l}</div>
                <b className="mt8 muted" style={{ display: "block" }}>
                  None yet
                </b>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
